import { useState, useEffect, useMemo, useRef } from "react";
import { useLocation } from "@tanstack/react-router";
import { useMutation, useConvexAuth, useQuery } from "convex/react";
import { useAtomValue } from "jotai";
import { api } from "../../../convex/_generated/api";
import { MessageList, type MessageData } from "./MessageList";
import { MessageInput } from "./MessageInput";
import { Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useStats } from "@/lib/hooks/useApi";
import { usePrivy } from "@privy-io/react-auth";
import { homeUiReadyAtom } from "@/lib/homeUiAtoms";
import { t } from "@/lib/i18n";
// import { formatAddress } from "@/lib/utils";

export function ChatWidget() {
  const location = useLocation();
  const { user, authenticated } = usePrivy();
  const { isAuthenticated: isConvexAuthenticated } = useConvexAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const [lastSeenMessageId, setLastSeenMessageId] = useState<string | null>(
    null,
  );
  const hasInitializedRef = useRef(false);
  const isHomeUiReady = useAtomValue(homeUiReadyAtom);
  const storeUser = useMutation(api.users.store);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const messages = (useQuery(api.messages.list) || []) as MessageData[];
  const currentUser = useQuery(api.users.current);

  // Fetch stats to get username
  const { data: stats } = useStats(user?.wallet?.address ?? "");

  const lastSeenStorageKey = useMemo(() => {
    if (!currentUser?._id) return null;
    return `cfl:chat:lastSeen:${currentUser._id}`;
  }, [currentUser?._id]);

  // Reset trackers when user context changes
  useEffect(() => {
    setLastSeenMessageId(null);
    setHasUnread(false);
    hasInitializedRef.current = false;
  }, [lastSeenStorageKey]);

  // Restore last seen on load
  useEffect(() => {
    if (!lastSeenStorageKey) return;
    const stored = localStorage.getItem(lastSeenStorageKey);
    if (stored) {
      setLastSeenMessageId(stored);
      hasInitializedRef.current = true;
    }
  }, [lastSeenStorageKey]);

  // Initialize last seen to latest message so backlog doesn't trigger unread badge
  useEffect(() => {
    if (hasInitializedRef.current) {
      return;
    }

    hasInitializedRef.current = true;
    if (!messages.length) return;

    const latestId = messages[messages.length - 1]._id;
    setLastSeenMessageId(latestId);
    if (lastSeenStorageKey) {
      localStorage.setItem(lastSeenStorageKey, latestId);
    }
  }, [messages, lastSeenStorageKey]);

  // Clear unread when opening
  useEffect(() => {
    if (!isOpen || !messages.length) return;
    const latestId = messages[messages.length - 1]._id;
    setLastSeenMessageId(latestId);
    if (lastSeenStorageKey) {
      localStorage.setItem(lastSeenStorageKey, latestId);
    }
    setHasUnread(false);
    hasInitializedRef.current = true;
  }, [isOpen, messages, lastSeenStorageKey]);

  // Detect new messages while closed
  useEffect(() => {
    if (!messages.length || isOpen || !hasInitializedRef.current) {
      return;
    }
    const latest = messages[messages.length - 1];
    if (lastSeenMessageId === null) {
      setHasUnread(latest.authorId !== currentUser?._id);
      return;
    }
    if (
      latest._id !== lastSeenMessageId &&
      latest.authorId !== currentUser?._id
    ) {
      setHasUnread(true);
    }
  }, [currentUser?._id, isOpen, lastSeenMessageId, messages]);

  // Sync user to Convex on load
  useEffect(() => {
    if (!stats) return;
    if (isConvexAuthenticated) {
      // We catch error here because storeUser might fail if auth is not fully ready or network issues
      storeUser({
        name: stats.username || t("chatWidget.anonymous"),
        profilePicture: stats.profilePicture,
        activeFrameUrl: stats.activeFrameUrl ?? undefined,
        domainType: stats.domainType ?? undefined,
      }).catch((e) => console.error("Failed to sync user:", e));
    }
  }, [isConvexAuthenticated, storeUser, stats, user?.wallet?.address]);

  // 1. Hide on specific routes, during the home splash, or if not authenticated
  // NOTE: Adjust these checks based on your exact route paths
  if (
    !authenticated ||
    location.pathname === "/" ||
    location.pathname === "/arena" ||
    location.pathname === "/referrer" ||
    (location.pathname === "/world" && !isHomeUiReady)
  ) {
    return null;
  }

  // 2. Floating Button
  if (!isOpen) {
    return (
      <div className="fixed bottom-[41svh] right-0 z-999">
        {hasUnread && (
          <span className="absolute top-0 -left-1 flex h-4 w-4 z-20">
            <span
              className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"
              style={{
                clipPath: `polygon(
                            0px calc(100% - 4px),
                            2px calc(100% - 4px),
                            2px calc(100% - 2px),
                            4px calc(100% - 2px),
                            4px 100%,
                            calc(100% - 4px) 100%,
                            calc(100% - 4px) calc(100% - 2px),
                            calc(100% - 2px) calc(100% - 2px),
                            calc(100% - 2px) calc(100% - 4px),
                            100% calc(100% - 4px),
                            100% 4px,
                            calc(100% - 2px) 4px,
                            calc(100% - 2px) 2px,
                            calc(100% - 4px) 2px,
                            calc(100% - 4px) 0px,
                            4px 0px,
                            4px 2px,
                            2px 2px,
                            2px 4px,
                            0px 4px
                          )`,
              }}
            ></span>
            <span
              className="relative inline-flex rounded-full h-4 w-4 bg-red-500 border border-white/20"
              style={{
                clipPath: `polygon(
                            0px calc(100% - 4px),
                            2px calc(100% - 4px),
                            2px calc(100% - 2px),
                            4px calc(100% - 2px),
                            4px 100%,
                            calc(100% - 4px) 100%,
                            calc(100% - 4px) calc(100% - 2px),
                            calc(100% - 2px) calc(100% - 2px),
                            calc(100% - 2px) calc(100% - 4px),
                            100% calc(100% - 4px),
                            100% 4px,
                            calc(100% - 2px) 4px,
                            calc(100% - 2px) 2px,
                            calc(100% - 4px) 2px,
                            calc(100% - 4px) 0px,
                            4px 0px,
                            4px 2px,
                            2px 2px,
                            2px 4px,
                            0px 4px
                          )`,
              }}
            ></span>
          </span>
        )}
        <Button
          onClick={() => setIsOpen(true)}
          className="h-16 w-8 z-100 bg-black rounded-none! text-white transition-all duration-300 hover:scale-110"
          size="icon"
          style={{
            clipPath: `
            polygon(
              0px calc(100% - 6px),      /* left bottom, above notch */
              3px calc(100% - 6px),
              3px calc(100% - 3px),
              6px calc(100% - 3px),
              6px 100%,                  /* bottom edge starts */

              100% 100%,                 /* bottom-right corner */
              100% 0px,                  /* top-right corner */

              6px 0px,                   /* start top-left notch */
              6px 3px,
              3px 3px,
              3px 6px,
              0px 6px                    /* left edge below top notch */
            )
            `,
          }}
        >
          <img
            src="https://cdn.navalabs.io/icons/chat_VVKdJ7.png"
            className="w-6"
            style={{
              imageRendering: "pixelated",
            }}
          />
        </Button>
      </div>
    );
  }

  // 3. Chat Window
  return (
    <div className="fixed dark-blue-container py-1 bottom-2 right-2 w-80 sm:w-96 h-[500px] bg-black shadow-2xl border border-gray-200 flex flex-col z-50 overflow-hidden">
      {/* Header */}
      <div className="bg-[#1D2B53] border-b border-[#0659B5]  px-4 py-1 flex justify-between items-center text-white shadow-sm">
        <div className="flex items-center space-x-2">
          <img
            src="https://cdn.navalabs.io/icons/chat_VVKdJ7.png"
            className="w-5"
            style={{
              imageRendering: "pixelated",
            }}
          />
          <h3 className="uppercase font-semibold">
            {t("chatWidget.globalChat")}
          </h3>
        </div>
        <button
          onClick={() => setIsOpen(false)}
          className="hover:bg-white/20 p-1.5 transition-colors"
        >
          <Minus className="h-5 w-5" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-hidden bg-[#1D2B53]">
        <MessageList messages={messages} currentUserId={currentUser?._id} />
      </div>

      {/* Input */}
      <div className="p-3 rounded-b-lg bg-[#1D2B53] border-t border-[#0659B5]">
        <MessageInput />
      </div>
    </div>
  );
}
